import React, { useState, useEffect } from "react";
import { Form, Input, Button, Card, Typography, message, Spin, Upload, Icon } from "antd";
import { useLocation } from "react-router-dom";
import { getData, createDataUnAuth } from "../../util/API";
import { uploadDirectToS3 } from "../../util/S3Functions";

const { Title, Paragraph, Text } = Typography;

const ACCEPT = "image/jpeg,image/png,application/pdf";
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
const MAX_BYTES = 2 * 1024 * 1024; // 2MB — S3 enforces the same cap on the presigned POST

/**
 * Public page a partner opens from the short link in the partner_acc_kyc WhatsApp.
 *
 * Identified by ?code=<short_code>, which the backend's /b/<code> redirect adds. The link is
 * dead once all four values are in, so this page refuses to render a completed submission.
 *
 * Bank name and branch are never asked: the backend derives them from the IFSC and sends them
 * back, shown here read-only so the partner can spot a wrong code.
 *
 * Two stages on one page:
 *   upload  - the cancelled cheque only. OCR then reads it.
 *   confirm - payee / account / IFSC, prefilled from OCR and fully editable.
 * OCR is a convenience, never a gate: if it reads nothing the confirm stage still opens with
 * blank fields, so an unreadable cheque never traps the partner.
 */
const PartnerBankDetails = (props) => {
  const { getFieldDecorator, validateFields } = props.form;
  const code = new URLSearchParams(useLocation().search).get("code");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invalid, setInvalid] = useState("");
  const [company, setCompany] = useState("");
  const [done, setDone] = useState(false);
  const [bankInfo, setBankInfo] = useState(null);
  const [cheque, setCheque] = useState(null);
  const [stage, setStage] = useState("upload");
  const [reading, setReading] = useState(false);
  // `read` distinguishes "OCR ran and found nothing" from "OCR never ran", so the form only
  // says it could not read a field when it actually tried.
  const [ocr, setOcr] = useState({ read: false, payee: "", account_number: "", ifsc: "" });
  // Bumped after every OCR pass. initialValue only applies when a field first mounts, so on a
  // second upload (the partner choosing a different file) the new values would never reach the
  // inputs -- they are already mounted. This drives the setFieldsValue below.
  const [justRead, setJustRead] = useState(0);

  useEffect(() => {
    if (!code) {
      setInvalid("This link is not valid. Please use the link sent to you on WhatsApp.");
      setLoading(false);
      return;
    }
    getData(`/partner/bank-form/?code=${code}`)
      .then((res) => {
        if (res?.data?.message === "success") {
          setCompany(res.data.partner_company || "");
          // A cheque already on file means the partner got this far before and came back --
          // after closing the tab, or losing signal part way. Resume at the confirm stage with
          // whatever they had saved rather than making them photograph the cheque again.
          if (res.data.cheque_uploaded) {
            setOcr({
              read: false,
              payee: res.data.payee || "",
              account_number: res.data.account_number || "",
              ifsc: res.data.ifsc || "",
            });
            setJustRead((n) => n + 1);
            setStage("confirm");
          }
        } else {
          setInvalid(res?.data?.message || "This link is no longer valid.");
        }
      })
      .catch(() => setInvalid("This link is no longer valid."))
      .finally(() => setLoading(false));
  }, [code]);

  useEffect(() => {
    if (!justRead) return;
    props.form.setFieldsValue({
      payee: ocr.payee || "",
      account_number: ocr.account_number || "",
      ifsc: ocr.ifsc || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justRead]);

  // Presigned URLs expire in 5 minutes, so one is requested here — when the file is chosen —
  // rather than on page load.
  const uploadCheque = async (file) => {
    const res = await createDataUnAuth("/partner/bank-form-cheque/", {
      code,
      file_name: file.name,
      file_type: file.type,
    });
    const data = res?.data;
    if (!data || data.message !== "success") {
      throw new Error(data?.message || "Could not start the upload.");
    }
    await uploadDirectToS3(file, data.presigned_data);
    const fin = await createDataUnAuth("/partner/bank-form-cheque/", {
      code,
      file_key: data.file_key,
      finish: true,
    });
    if (fin?.data?.message !== "success") {
      throw new Error(fin?.data?.message || "Could not save the cheque.");
    }
  };

  // Upload the cheque, read it, then open the confirm stage. OCR failing is not an error the
  // partner should ever see — it just means the fields start empty.
  const handleCheque = async (file) => {
    setReading(true);
    // Clear anything read from a previous file: on "Choose a different file" the old bank and
    // branch stayed on screen under an empty IFSC field, describing the wrong cheque.
    setBankInfo(null);
    setOcr({ read: false, payee: "", account_number: "", ifsc: "" });
    setBankInfo(null);
    props.form.setFieldsValue({ payee: "", account_number: "", ifsc: "" });
    try {
      await uploadCheque(file);
      setCheque(file);
    } catch (ex) {
      // axios surfaces S3 rejections as "Request failed with status code 400", which means
      // nothing to a partner. Keep the detail in the console, show something actionable.
      console.error("cheque upload failed:", ex);
      message.error(
        "Could not upload the cheque. Please check your connection and try again."
      );
      setReading(false);
      return;
    }

    try {
      const res = await createDataUnAuth("/partner/bank-form-ocr/", { code });
      const d = res?.data;
      if (d && d.message === "success") {
        // Must go through initialValue, not setFieldsValue: in antd v3 a field only exists
        // once getFieldDecorator has rendered it, and these fields are still unmounted while
        // the upload stage is showing.
        setOcr({
          read: true,
          payee: d.payee || "",
          account_number: d.account_number || "",
          ifsc: d.ifsc || "",
        });
        if (d.bank_name) setBankInfo({ bank: d.bank_name, branch: d.branch });
      }
    } catch (ex) {
      // Deliberately silent: the confirm stage opens either way.
    }

    setReading(false);
    setStage("confirm");
    setJustRead((n) => n + 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    validateFields(async (err, values) => {
      if (err) return;
      setSaving(true);
      try {
        // The cheque is already uploaded — it is what opened this stage.
        const res = await createDataUnAuth("/partner/bank-form/", {
          code,
          payee: values.payee,
          account_number: values.account_number,
          ifsc: values.ifsc,
        });
        if (res?.data?.message !== "success") {
          message.error(res?.data?.message || "Could not save your details.");
          setSaving(false);
          return;
        }
        setBankInfo({ bank: res.data.bank_name, branch: res.data.branch });
        setDone(true);
      } catch (ex) {
        message.error(ex.message || "Something went wrong. Please try again.");
      } finally {
        setSaving(false);
      }
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  const wrap = (children) => (
    <div style={{ maxWidth: 520, margin: "0 auto", padding: 16 }}>
      <Card>{children}</Card>
    </div>
  );

  if (invalid) return wrap(<Paragraph style={{ marginBottom: 0 }}>{invalid}</Paragraph>);

  if (done) {
    return wrap(
      <div style={{ textAlign: "center" }}>
        <Icon type="check-circle" theme="filled" style={{ fontSize: 44, color: "#52c41a" }} />
        <Title level={4} style={{ marginTop: 16 }}>Thank you</Title>
        <Paragraph>
          Your bank details have been received. This link is now closed.
        </Paragraph>
      </div>
    );
  }

  const chequeField = (
    <Form.Item label="Cancelled cheque (JPG, PNG or PDF, up to 2MB)">
      <Upload
        accept={ACCEPT}
        disabled={reading}
        beforeUpload={(file) => {
          if (!ALLOWED_TYPES.includes(file.type)) {
            message.error("Please attach a JPG, PNG or PDF.");
            return Upload.LIST_IGNORE;
          }
          if (file.size > MAX_BYTES) {
            message.error("That file is over 2MB. Please attach a smaller photo or scan.");
            return Upload.LIST_IGNORE;
          }
          handleCheque(file);
          return false; // uploaded by handleCheque, not by antd
        }}
        onRemove={() => setCheque(null)}
        fileList={cheque ? [{ uid: "1", name: cheque.name, status: "done" }] : []}
      >
        <Button disabled={reading}>
          <Icon type="upload" /> {cheque ? "Choose a different file" : "Choose file"}
        </Button>
      </Upload>
    </Form.Item>
  );

  if (stage === "upload") {
    return wrap(
      <>
        <Title level={4} style={{ marginBottom: 4 }}>Bank details</Title>
        {company ? <Paragraph type="secondary">{company}</Paragraph> : null}
        <Paragraph>
          Your account number is not yet activated in our system. Start by attaching a photo or
          scan of your cancelled cheque — we will read the details off it for you.
        </Paragraph>
        <Form layout="vertical">{chequeField}</Form>
        {reading ? (
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <Spin />
            <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
              Reading your cheque…
            </Text>
          </div>
        ) : null}
      </>
    );
  }

  return wrap(
    <>
      <Title level={4} style={{ marginBottom: 4 }}>Check your details</Title>
      {company ? <Paragraph type="secondary">{company}</Paragraph> : null}
      <Paragraph>
        {ocr.read && (ocr.payee || ocr.account_number || ocr.ifsc)
          ? "We read these from your cheque. Please correct anything that is wrong before submitting."
          : "Please fill in your bank details below."}
      </Paragraph>

      <Form layout="vertical" onSubmit={handleSubmit}>
        <Form.Item
          label="Payee name (as on the bank account)"
          extra={
            ocr.read && !ocr.payee
              ? "We could not read this from your cheque — please type it."
              : undefined
          }
        >
          {getFieldDecorator("payee", {
            initialValue: ocr.payee,
            rules: [
              { required: true, message: "Please enter the payee name" },
              // Same rule the backend and the payment export apply, shown inline rather than
              // as a server error after submit.
              {
                pattern: /^[A-Za-z0-9 ]+$/,
                message: "Letters, numbers and spaces only — no dots, hyphens or &",
              },
            ],
          })(<Input placeholder="Payee name" />)}
        </Form.Item>

        <Form.Item
          label="Account number"
          extra={
            ocr.read && !ocr.account_number
              ? "We could not read this from your cheque — please type it."
              : undefined
          }
        >
          {getFieldDecorator("account_number", {
            initialValue: ocr.account_number,
            rules: [
              { required: true, message: "Please enter the account number" },
              { pattern: /^[0-9]{6,20}$/, message: "6 to 20 digits, numbers only" },
            ],
          })(<Input placeholder="Account number" inputMode="numeric" />)}
        </Form.Item>

        <Form.Item
          label="IFSC code"
          extra={bankInfo ? `${bankInfo.bank} — ${bankInfo.branch}` : undefined}
        >
          {getFieldDecorator("ifsc", {
            initialValue: ocr.ifsc,
            normalize: (v) => (v || "").toUpperCase(),
            rules: [
              { required: true, message: "Please enter the IFSC code" },
              { pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/, message: "Enter a valid 11-character IFSC" },
            ],
          })(<Input placeholder="e.g. SBIN0001234" maxLength={11} />)}
        </Form.Item>

        {chequeField}

        <Button type="primary" htmlType="submit" loading={saving} block>
          Submit
        </Button>
        <Text type="secondary" style={{ display: "block", marginTop: 12, fontSize: 12 }}>
          This link can only be used once.
        </Text>
      </Form>
    </>
  );
};

export default Form.create()(PartnerBankDetails);
